import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router';
import { JSONSchema7 } from 'json-schema';
import { withTheme } from '@rjsf/core';
import { Theme as AntDTheme } from '@rjsf/antd';
import { ErrorBoundary } from '@allenai/varnish';
import message from 'antd/es/message';
import Button from 'antd/es/button';
import isEqual from 'lodash.isequal';

import { EditablePageHeader, FieldTemplate } from '../components';
import { UiWidgets } from '../data';
import { createAsset, getAsset, updateAsset } from '../api';
import { displayDate, UiKey, UiSchemaKey, getCategoryAndAssetDetails } from '../utils/util';

// Load types and schema from OpenAPI. See project README.md for info on how these files are formed.
import { components } from '../../build/openapiTypes';
import { MaxWidthContainer } from '../components/Layout';
import { ChangeDisclaimer } from '../components/form/ChangeDisclaimer';

// use antd theme
const Form = withTheme(AntDTheme);

interface Params {
    category: string;
    id?: string;
}

const UpsertAssetImpl = ({ match, history }: RouteComponentProps<Params>) => {
    const {
        categoryData,
        assetData,
        assetSchema: originalAssetSchema,
        assetNameFromType,
    } = getCategoryAndAssetDetails(match.params.category, true);

    // grab ts type for the asset
    // omitting string because we want only objects
    type Asset = Omit<components['schemas'][typeof assetNameFromType], string>;

    const [data, setData] = useState<Asset>();
    useEffect(() => {
        if (match.params.id) {
            getAsset<Asset>(match.params.category, match.params.id)
                .then((d) => setData(d))
                .catch((e) => message.error(`Error loading data: ${e.message}`));
        }
    }, [match]);

    // generating uiSchema from our backend data...
    const uiAssetSchema: any = {};
    const assetSchemaProperties: any = {};
    if (originalAssetSchema.properties) {
        Object.entries(originalAssetSchema.properties).forEach(([key, val]: [string, any]) => {
            uiAssetSchema[key] = {};
            uiAssetSchema[key][UiKey.Description] = val.description;
            uiAssetSchema[key][UiKey.Help] = val[UiSchemaKey.Help];
            uiAssetSchema[key][UiKey.Placeholder] =
                (val[UiSchemaKey.Widget] === UiWidgets.Date ||
                    val[UiSchemaKey.Widget] === UiWidgets.DateTime) &&
                val[UiSchemaKey.WidgetProps]?.format
                    ? displayDate(
                          val.example,
                          val[UiSchemaKey.WidgetProps]?.format,
                          val[UiSchemaKey.WidgetProps]?.displayFormat
                      )
                    : val.example;
            uiAssetSchema[key][UiKey.Rows] = val[UiSchemaKey.Rows];
            uiAssetSchema[key][UiKey.Widget] = val[UiSchemaKey.Widget];
        });
        Object.entries(originalAssetSchema.properties).forEach(([key, val]: [string, any]) => {
            // remove these so they don't pollute the ui
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { description, example, ...rest } = val;
            assetSchemaProperties[key] = rest;
        });
    }
    // force update of react element be updating the original object
    const assetSchema = { ...originalAssetSchema, properties: assetSchemaProperties };

    // call api with formData
    const onSubmit = ({ formData }: any) => {
        if (match.params.id) {
            updateAsset(match.params.category, match.params.id, formData).catch((e) =>
                message.error(`Error saving data: ${e.message}`)
            );
        } else {
            createAsset(match.params.category, formData).catch((e) =>
                message.error(`Error saving data: ${e.message}`)
            );
        }
        history.goBack();
    };

    return (
        <MaxWidthContainer>
            <EditablePageHeader
                iconName={categoryData.iconName}
                iconType={categoryData.iconType}
                subLabel={assetData.label}
                history={history}
                label={match.params.id ? 'Update' : 'New'}
            />

            <DynamicForm
                data={data}
                assetSchema={assetSchema}
                uiAssetSchema={uiAssetSchema}
                onCancel={() => history.goBack()}
                onSubmit={onSubmit}
            />
        </MaxWidthContainer>
    );
};

interface DynamicFormProps {
    data: Omit<components['schemas'][keyof components['schemas']], string> | undefined;
    assetSchema: JSONSchema7;
    uiAssetSchema: any;
    onCancel: () => void;
    onSubmit: ({ formData }: { formData: any }) => void;
}

// This form is memoized so we can control when re-renders occur.
// Since the form data is stored in the form's context, if the
// form re-renders, any unsaved drafted data will be lost.
// So we need to be careful about when we allow this to happen.
const DynamicForm = React.memo(
    ({ data, assetSchema, uiAssetSchema, onCancel, onSubmit }: DynamicFormProps) => {
        return (
            <SpacedForm
                formData={data}
                schema={assetSchema}
                uiSchema={uiAssetSchema}
                onSubmit={onSubmit}
                FieldTemplate={FieldTemplate}>
                <ChangeDisclaimer />
                <BtnGroup>
                    <Button onClick={() => onCancel()} danger>
                        Cancel
                    </Button>
                    {/* This is a normal button so we can use the type="submit" */}
                    <button className="ant-btn ant-btn-primary" type="submit">
                        Save
                    </button>
                </BtnGroup>
            </SpacedForm>
        );
    },
    (prevProps, nextProps) => {
        // specifically checking for these differences so we don't get
        // destructive re-renders on screen resizing
        return (
            isEqual(prevProps.data, nextProps.data) &&
            isEqual(prevProps.assetSchema, nextProps.assetSchema) &&
            isEqual(prevProps.uiAssetSchema, nextProps.uiAssetSchema)
        );
    }
);

const BtnGroup = styled.div`
    float: right;
    button {
        margin-left: ${({ theme }) => theme.spacing.md};
    }
`;

const SpacedForm = styled(Form)`
    &&& {
        .ant-form-item-label {
            width: 100%;
            text-align: left;
            padding-bottom: 0;
            label {
                height: unset;
            }
        }
        .form-group {
            margin-bottom: ${({ theme }) => theme.spacing.md};
        }
    }

    // the button css is to force the form's use of html button to look like antd
    // this is what shellac is for, but i dont want to bring it in for this
    .ant-btn {
        display: inline-block;
        background: ${({ theme }) => theme.palette.primary.default};
        color: ${({ theme }) => theme.palette.text.contrast};
        cursor: pointer;
        padding: ${({ theme }) => `0 ${theme.spacing.md}`};
        border-radius: ${({ theme }) => theme.shape.borderRadius.default};
        border: none;
        font-weight: ${({ theme }) => theme.typography.font.weight.bold};
        font-size: ${({ theme }) => theme.typography.textStyles.small.fontSize};
        white-space: nowrap;
        font-weight: bold;
        font-family: inherit;

        &:hover {
            background: ${({ theme }) => theme.palette.primary.dark};
            text-decoration: none;
        }
    }

    .ant-btn-primary {
        background: ${({ theme }) => theme.palette.primary.default};
        color: ${({ theme }) => theme.palette.text.contrast};
    }

    .ant-btn-dangerous {
        background: ${({ theme }) => theme.color.R7};
        &:hover {
            background: ${({ theme }) => theme.color.R9};
        }
    }

    button:disabled {
        opacity: 0.5;
        background: ${({ theme }) => theme.palette.primary.veryDark};
        cursor: default;
    }
`;

export const UpsertAsset = (props: RouteComponentProps<Params>) => {
    return (
        <ErrorBoundary key={props.match.params.category}>
            <UpsertAssetImpl {...props} />
        </ErrorBoundary>
    );
};
